import React, { useEffect, useState } from "react";
import { IonizableLipidTable, TableData } from "../components/ilTable";
import TablePagination from "../components/table/components/TablePagination";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import Navbar from "../components/ui/navbar";
import { Backend } from "../url";

const IonizableTablePage: React.FC = () => {
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [experimentalIds, setExperimentalIds] = useState<string[]>([]);
  const [selectedExperimentalId, setSelectedExperimentalId] =
    useState<string>("");
  const pageSizeOptions = [10, 20, 50, 100];

  useEffect(() => {
    const fetchExperimentalIds = async () => {
      try {
        const url = `${Backend}/search/metadata`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch experimental IDs: ${response.statusText}`
          );
        }
        const data = await response.json();

        const ids = data.map(
          (item: { experiment_id: string }) => item.experiment_id
        );
        setExperimentalIds(ids || []);
      } catch (err) {
        console.error("Failed to load experimental IDs:", err);
      }
    };

    fetchExperimentalIds();
  }, []);

  useEffect(() => {
    const fetchTableData = async () => {
      setLoading(true);
      try {
        let url = `${Backend}/search/ionizable?page=${currentPage}&page_size=${pageSize}`;
        if (selectedExperimentalId) {
          url += `&experimental_id=${selectedExperimentalId}`;
        }
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }
        const data = await response.json();
        setTableData(data);
      } catch (err) {
        console.error("Failed to load data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTableData();
  }, [currentPage, pageSize, selectedExperimentalId]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleExperimentalIdChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedExperimentalId(e.target.value);
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setSelectedExperimentalId("");
    setCurrentPage(1);
  };

  return (
    <>
      <div className="relative flex-shrink-0 w-full py-8 border-b border-1">
        <Navbar />
      </div>
      <div className="flex justify-center">
        <div className="w-full px-4 md:overflow-x-auto">
          <div className="flex flex-col items-center justify-between my-6 md:flex-row">
            <h1 className="mb-4 text-2xl font-bold md:mb-0">Ionizable Lipid</h1>

            <div className="flex items-center space-x-2">
              <label
                htmlFor="experimental-id-filter"
                className="text-sm font-medium text-gray-700"
              >
                Filter by Library Code:
              </label>
              <select
                id="experimental-id-filter"
                value={selectedExperimentalId}
                onChange={handleExperimentalIdChange}
                className="px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                {experimentalIds.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>

              {selectedExperimentalId && (
                <button
                  onClick={handleClearFilter}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {selectedExperimentalId && (
            <div className="flex items-center justify-between p-2 mb-4 rounded-lg bg-blue-50">
              <span className="text-sm text-blue-700">
                Filtered by Library Code:{" "}
                <strong>{selectedExperimentalId}</strong>
              </span>
              <button
                onClick={handleClearFilter}
                className="text-sm text-blue-700 hover:text-blue-900"
              >
                ×
              </button>
            </div>
          )}

          {tableData && tableData.count > 0 && (
            <TablePagination
              paginationConfig={{
                currentPage,
                pageSize,
                totalItems: tableData.count,
                pageSizeOptions,
              }}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}

          {loading ? (
            <LoadingSpinner />
          ) : (
            <IonizableLipidTable data={tableData} />
          )}

          {tableData && tableData.count > pageSizeOptions[0] && (
            <TablePagination
              paginationConfig={{
                currentPage,
                pageSize,
                totalItems: tableData.count,
                pageSizeOptions,
              }}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}

          {tableData && tableData.count === 0 && (
            <div className="p-8 text-center">
              <p className="text-lg text-gray-500">No results found.</p>
              {selectedExperimentalId && (
                <button
                  onClick={handleClearFilter}
                  className="px-4 py-2 mt-2 text-white bg-blue-500 rounded hover:bg-blue-600"
                >
                  Clear Filter
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default IonizableTablePage;
